import React, { useEffect, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { addOutline, busOutline } from "ionicons/icons";
import { useHistory } from "react-router";

import { ReembolsoMes, ReembolsoReclamo } from "src/utils/interfaces";

import ReembolsosAPI from "src/services/reembolsos";

import Header from "src/components/Header";

// Colores de chip por estado (mismo criterio que EMSYS)
const COLOR_ESTADO: Record<string, string> = {
  GENERADO: "warning",
  APROBADO: "success",
  RECHAZADO: "danger",
  OBSERVADO: "tertiary",
  PENDIENTE: "warning",
};

const LABEL_ESTADO: Record<string, string> = {
  GENERADO: "Pendiente",
  APROBADO: "Aprobado",
  RECHAZADO: "Rechazado",
  OBSERVADO: "En revisión",
  PENDIENTE: "Pendiente",
};

const formatearMes = (mes: string) => {
  // mes viene como 'YYYY-MM'
  const [anho, mesNum] = mes.split("-");
  const nombres = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];
  const idx = parseInt(mesNum, 10) - 1;
  return `${nombres[idx] || mesNum} ${anho}`;
};

const formatearMonto = (monto?: number) =>
  monto === null || monto === undefined ? "-" : `${Math.round(monto).toLocaleString("es-PY")} Gs.`;

// Fecha local 'YYYY-MM-DD' (sin pasar por UTC, que corre el dia a la noche).
const aFechaISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Rango por defecto: la semana actual y la anterior (lunes pasado -> hoy).
const rangoPorDefecto = () => {
  const hoy = new Date();
  const desde = new Date(hoy);
  desde.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7) - 7);
  return { desde: aFechaISO(desde), hasta: aFechaISO(hoy) };
};

const ReembolsosPasajes: React.FC = () => {
  const [meses, setMeses] = useState<ReembolsoMes[]>([]);
  const [reclamos, setReclamos] = useState<ReembolsoReclamo[]>([]);
  const [puedeReclamar, setPuedeReclamar] = useState(false);
  const [alcanzado, setAlcanzado] = useState(true);
  const [reclamoCerrado, setReclamoCerrado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [desde, setDesde] = useState(() => rangoPorDefecto().desde);
  const [hasta, setHasta] = useState(() => rangoPorDefecto().hasta);
  const [present] = useIonToast();
  const history = useHistory();

  const rangoInvalido = Boolean(desde && hasta && desde > hasta);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta]);

  const cargar = () => {
    if (!desde || !hasta || rangoInvalido) {
      return;
    }
    setCargando(true);
    ReembolsosAPI.getReembolsos(desde, hasta)
      .then((response) => {
        setMeses(response.data.meses || []);
        setReclamos(response.data.reclamos || []);
        setPuedeReclamar(Boolean(response.data.puede_reclamar));
        setAlcanzado(response.data.alcanzado !== false);
        setReclamoCerrado(response.data.reclamo_abierto === false);
      })
      .catch(() =>
        present({
          message: "Ocurrió un error al cargar los reembolsos",
          duration: 5000,
        })
      )
      .finally(() => setCargando(false));
  };

  return (
    <IonPage>
      <Header titulo="Reembolso de Pasajes" />

      <IonContent id="reembolsos-pasajes">
        <div className="container mb-4">
          {cargando && (
            <div className="ion-text-center ion-padding">
              <IonSpinner name="crescent" />
            </div>
          )}

          {!cargando && puedeReclamar && (
            <IonButton
              expand="block"
              color="primary"
              onClick={() => history.push("/reembolsos-pasajes/reclamar")}
            >
              <IonIcon slot="start" icon={addOutline} />
              Reclamar traslado faltante
            </IonButton>
          )}

          {!cargando && reclamoCerrado && meses.length > 0 && (
            <p className="ion-text-center" style={{ fontSize: "13px", opacity: 0.7 }}>
              El período para enviar reclamos de pasajes está cerrado.
            </p>
          )}

          {alcanzado && (
          <IonCard>
            <IonCardContent style={{ paddingTop: "4px", paddingBottom: "8px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <IonItem lines="none" style={{ flex: 1 }}>
                  <IonLabel position="stacked">Desde</IonLabel>
                  <IonDatetime
                    displayFormat="DD/MM/YYYY"
                    pickerFormat="DD/MM/YYYY"
                    cancelText="Cancelar"
                    doneText="Listo"
                    value={desde}
                    onIonChange={(e) => setDesde((e.detail.value || "").substring(0, 10))}
                  />
                </IonItem>
                <IonItem lines="none" style={{ flex: 1 }}>
                  <IonLabel position="stacked">Hasta</IonLabel>
                  <IonDatetime
                    displayFormat="DD/MM/YYYY"
                    pickerFormat="DD/MM/YYYY"
                    cancelText="Cancelar"
                    doneText="Listo"
                    value={hasta}
                    onIonChange={(e) => setHasta((e.detail.value || "").substring(0, 10))}
                  />
                </IonItem>
              </div>
              {rangoInvalido && (
                <p className="ion-text-center" style={{ color: "var(--ion-color-danger)", fontSize: "13px", margin: "4px 0 0 0" }}>
                  La fecha "Desde" no puede ser mayor que la fecha "Hasta".
                </p>
              )}
            </IonCardContent>
          </IonCard>
          )}

          {!cargando && meses.length === 0 && (
            <IonCard>
              <IonCardContent>
                <p className="ion-text-center">
                  {alcanzado
                    ? "No hay reembolsos de pasajes en el período seleccionado."
                    : "Tu cargo no está alcanzado por el reembolso de pasajes."}
                </p>
              </IonCardContent>
            </IonCard>
          )}

          {!cargando &&
            meses.map((mes) => (
              <IonCard key={mes.mes}>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: "18px" }}>
                    <IonIcon icon={busOutline} /> {formatearMes(mes.mes)}
                  </IonCardTitle>
                  <p style={{ margin: "6px 0 0 0", fontSize: "14px" }}>
                    Aprobado: <strong>{formatearMonto(mes.total_aprobado)}</strong>
                    {" · "}
                    Pendiente: <strong>{formatearMonto(mes.total_pendiente)}</strong>
                  </p>
                </IonCardHeader>
                <IonCardContent style={{ paddingTop: 0 }}>
                  <IonList>
                    {mes.traslados.map((t) => (
                      <IonItem
                        key={t.id}
                        button
                        detail
                        onClick={() => history.push(`/reembolsos-pasajes/detalle/${t.id}`)}
                      >
                        <IonLabel>
                          <h3>
                            {t.fecha}
                            {t.cant_buses ? ` · ${t.cant_buses} bus(es)` : ""}
                          </h3>
                          <p>
                            {t.ps_origen || "?"} → {t.ps_destino || "?"}
                          </p>
                          <p>{formatearMonto(t.monto)}</p>
                        </IonLabel>
                        <IonBadge slot="end" color={COLOR_ESTADO[t.estado] || "medium"}>
                          {LABEL_ESTADO[t.estado] || t.estado}
                        </IonBadge>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            ))}

          {!cargando && reclamos.length > 0 && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle style={{ fontSize: "18px" }}>Mis reclamos</IonCardTitle>
              </IonCardHeader>
              <IonCardContent style={{ paddingTop: 0 }}>
                <IonList>
                  {reclamos.map((r) => (
                    <IonItem key={r.id}>
                      <IonLabel className="ion-text-wrap">
                        <h3>{r.fecha}</h3>
                        <p>
                          {(r.ps_origen || r.ps_texto || "?") + " → " + (r.ps_destino || "?")}
                        </p>
                        {r.respuesta_gestion && <p>Respuesta: {r.respuesta_gestion}</p>}
                      </IonLabel>
                      <IonBadge slot="end" color={COLOR_ESTADO[r.estado] || "medium"}>
                        {LABEL_ESTADO[r.estado] || r.estado}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ReembolsosPasajes;
